import type { Room } from 'livekit-client';
import { Track } from 'livekit-client';
import { averageIceRttMs, getIceTransportSummaries } from './iceTransportSummary';
import type { MeetingTelemetryPayload, TelemetrySampleEvent } from './meetingTelemetryTypes';
import { MEETING_TELEMETRY_SCHEMA_VERSION } from './meetingTelemetryTypes';
import { fetchTrackStats, type RtcMediaStatsData, type RtcStatsPrevEntry } from './rtcTrackStats';

export interface RemotePrevEntry {
  cameraVideo: RtcStatsPrevEntry | null;
  audio: RtcStatsPrevEntry | null;
}

export interface TelemetryPrevBundle {
  cameraVideo: RtcStatsPrevEntry | null;
  screenVideo: RtcStatsPrevEntry | null;
  audio: RtcStatsPrevEntry | null;
  remoteByIdentity: Record<string, RemotePrevEntry>;
}

function parseMetadata(raw: string | undefined): Record<string, unknown> | undefined {
  if (!raw) return undefined;
  try {
    const v = JSON.parse(raw) as unknown;
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      return v as Record<string, unknown>;
    }
  } catch {
    // ignore
  }
  return undefined;
}

function getRemotePrev(prev: TelemetryPrevBundle, identity: string): RemotePrevEntry {
  let e = prev.remoteByIdentity[identity];
  if (!e) {
    e = { cameraVideo: null, audio: null };
    prev.remoteByIdentity[identity] = e;
  }
  return e;
}

function pruneRemotePrev(room: Room, prev: TelemetryPrevBundle) {
  const alive = new Set<string>();
  room.remoteParticipants.forEach((_, k) => alive.add(k));
  for (const k of Object.keys(prev.remoteByIdentity)) {
    if (!alive.has(k)) {
      delete prev.remoteByIdentity[k];
    }
  }
}

function maxDefined(...vals: (number | undefined)[]): number | undefined {
  const nums = vals.filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  return nums.length ? Math.max(...nums) : undefined;
}

function sumDefined(...vals: (number | undefined)[]): number | undefined {
  const nums = vals.filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  if (!nums.length) return undefined;
  return nums.reduce((a, b) => a + b, 0);
}

/**
 * Builds one telemetry sample for the **local** participant (this browser only).
 * `prev` must persist between calls (same refs object) so bitrates stay meaningful.
 */
export async function collectMeetingTelemetrySnapshot(
  room: Room,
  roomName: string,
  clientInstanceId: string,
  prev: TelemetryPrevBundle,
  takeSampleEvents?: () => TelemetrySampleEvent[],
): Promise<MeetingTelemetryPayload> {
  const sampledAtStart = takeSampleEvents?.() ?? [];

  const lp = room.localParticipant;
  const camOn = lp.isCameraEnabled;
  const micOn = lp.isMicrophoneEnabled;
  const screenOn = lp.isScreenShareEnabled;

  const prevCam = {
    get current() {
      return prev.cameraVideo;
    },
    set current(v: RtcStatsPrevEntry | null) {
      prev.cameraVideo = v;
    },
  };
  const prevAud = {
    get current() {
      return prev.audio;
    },
    set current(v: RtcStatsPrevEntry | null) {
      prev.audio = v;
    },
  };
  const prevScreen = {
    get current() {
      return prev.screenVideo;
    },
    set current(v: RtcStatsPrevEntry | null) {
      prev.screenVideo = v;
    },
  };

  const cameraStats =
    camOn || micOn ? await fetchTrackStats(lp, Track.Source.Camera, prevCam, prevAud, true) : null;

  let screenStats = screenOn
    ? await fetchTrackStats(lp, Track.Source.ScreenShare, prevScreen, prevAud, false)
    : null;
  // First poll after enabling screen often has no byte delta; a short second read yields real bitrate.
  if (
    screenOn &&
    screenStats &&
    screenStats.videoBitrateBps === undefined &&
    typeof window !== 'undefined'
  ) {
    await new Promise<void>((resolve) => {
      window.setTimeout(resolve, 2000);
    });
    screenStats = await fetchTrackStats(lp, Track.Source.ScreenShare, prevScreen, prevAud, false);
  }

  const ice = await getIceTransportSummaries(room);
  pruneRemotePrev(room, prev);

  const mediaDownlink: NonNullable<MeetingTelemetryPayload['mediaDownlink']> = [];
  for (const rp of room.remoteParticipants.values()) {
    const rent = getRemotePrev(prev, rp.identity);
    const prevV = {
      get current() {
        return rent.cameraVideo;
      },
      set current(v: RtcStatsPrevEntry | null) {
        rent.cameraVideo = v;
      },
    };
    const prevA = {
      get current() {
        return rent.audio;
      },
      set current(v: RtcStatsPrevEntry | null) {
        rent.audio = v;
      },
    };
    const cam = await fetchTrackStats(rp, Track.Source.Camera, prevV, prevA, true);
    mediaDownlink.push({ identity: rp.identity, name: rp.name, camera: cam });
  }

  const uplinkVideoLoss = maxDefined(cameraStats?.videoPacketLoss, screenStats?.videoPacketLoss);
  const uplinkVideoJitter = maxDefined(cameraStats?.videoJitterSec, screenStats?.videoJitterSec);
  const uplinkVideoBps = sumDefined(cameraStats?.videoBitrateBps, screenStats?.videoBitrateBps);

  const uplinkAudioLoss = cameraStats?.audioPacketLoss;
  const uplinkAudioJitter = cameraStats?.audioJitterSec;
  const uplinkAudioBps = cameraStats?.audioBitrateBps;

  const downVideoLoss = maxDefined(...mediaDownlink.map((d) => d.camera?.videoPacketLoss));
  const downAudioLoss = maxDefined(...mediaDownlink.map((d) => d.camera?.audioPacketLoss));
  const downVideoJitter = maxDefined(...mediaDownlink.map((d) => d.camera?.videoJitterSec));
  const downAudioJitter = maxDefined(...mediaDownlink.map((d) => d.camera?.audioJitterSec));
  const downVideoBps = sumDefined(...mediaDownlink.map((d) => d.camera?.videoBitrateBps));
  const downAudioBps = sumDefined(...mediaDownlink.map((d) => d.camera?.audioBitrateBps));

  const rttMsAvg = averageIceRttMs(ice);
  const iceLocalCandidateType =
    ice.publisher || ice.subscriber
      ? {
          publisher: ice.publisher?.local?.candidateType,
          subscriber: ice.subscriber?.local?.candidateType,
        }
      : undefined;

  const network: MeetingTelemetryPayload['network'] = {
    rttMsAvg,
    iceLocalCandidateType,
    packetLossPercent: {
      uplink: { video: uplinkVideoLoss, audio: uplinkAudioLoss },
      downlink: { video: downVideoLoss, audio: downAudioLoss },
    },
    jitterSeconds: {
      uplink: { video: uplinkVideoJitter, audio: uplinkAudioJitter },
      downlink: { video: downVideoJitter, audio: downAudioJitter },
    },
    bitrateBps: {
      uplink: { video: uplinkVideoBps, audio: uplinkAudioBps },
      downlink: { video: downVideoBps, audio: downAudioBps },
    },
  };

  const trimmed =
    sampledAtStart.length > 48
      ? sampledAtStart.slice(sampledAtStart.length - 48)
      : sampledAtStart;

  let virtualBackgroundActive: boolean | undefined;
  let noiseCancellationEnabled: boolean | undefined;
  if (typeof window !== 'undefined') {
    try {
      const raw = localStorage.getItem('evolutto-user-settings');
      if (raw) {
        const parsed = JSON.parse(raw) as {
          backgroundType?: string;
          noiseSuppression?: boolean;
        };
        virtualBackgroundActive =
          parsed.backgroundType === 'blur' || parsed.backgroundType === 'image';
        if (typeof parsed.noiseSuppression === 'boolean') {
          noiseCancellationEnabled = parsed.noiseSuppression;
        }
      }
    } catch {
      /* ignore */
    }
  }

  let activeRemoteVideoSubscriptions = 0;
  room.remoteParticipants.forEach((participant) => {
    participant.videoTrackPublications.forEach((publication) => {
      if (publication.isSubscribed && publication.track) {
        activeRemoteVideoSubscriptions += 1;
      }
    });
  });

  const nav = typeof navigator !== 'undefined' ? navigator : undefined;
  const deviceMemoryGb = nav
    ? (nav as Navigator & { deviceMemory?: number }).deviceMemory
    : undefined;

  // Homolog/dev: sinaliza pico de tela acima do alvo do plano (~1,5 Mbps Normal).
  const screenKbps = screenStats?.videoKbps;
  if (
    typeof screenKbps === 'number' &&
    screenKbps > 1800 &&
    typeof console !== 'undefined' &&
    process.env.NODE_ENV !== 'production'
  ) {
    console.info('[meeting-telemetry] screenShare videoKbps high', {
      videoKbps: screenKbps,
      height: screenStats?.height,
      fps: screenStats?.fps,
      freezeCount: screenStats?.freezeCount,
    });
  }

  return {
    schemaVersion: MEETING_TELEMETRY_SCHEMA_VERSION,
    recordedAt: new Date().toISOString(),
    clientInstanceId,
    room: {
      name: roomName,
      state: room.state,
      numParticipants: 1 + room.remoteParticipants.size,
    },
    participant: {
      identity: lp.identity,
      name: lp.name,
      metadata: parseMetadata(lp.metadata),
      connectionQuality: String(lp.connectionQuality),
      audioLevel: typeof lp.audioLevel === 'number' ? lp.audioLevel : undefined,
    },
    tracks: {
      camera: { enabled: camOn },
      microphone: { enabled: micOn },
      screenShare: { enabled: screenOn },
    },
    media: {
      camera: cameraStats,
      screenShare: screenStats,
    },
    ice: Object.keys(ice).length ? ice : undefined,
    network,
    mediaDownlink: mediaDownlink.length ? mediaDownlink : undefined,
    sampleEvents: trimmed.length > 0 ? trimmed : undefined,
    client: {
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      language: typeof navigator !== 'undefined' ? navigator.language : '',
      hardwareConcurrency:
        typeof navigator !== 'undefined' ? navigator.hardwareConcurrency : undefined,
      deviceMemoryGb,
      virtualBackgroundActive,
      noiseCancellationEnabled,
      activeRemoteVideoSubscriptions,
    },
  };
}
